import Select, { StylesConfig } from "react-select";
import { CommonAgentSummary } from "../types";
import { getFriendlyAgentName } from "../util";
import { useReactSelectTheme } from "@bagel-web/components";

type AgentOption = {
  value: string;
  friendlyName: string | null;
  emoji: string | null;
  label: string;
};

const getIconStyling = (hasEmoji: boolean) => {
  return {
    marginRight: "8px",
    fontSize: "16px",
    ...(!hasEmoji && {
      marginRight: "8px",
      fontFamily: "bootstrap-icons",
      fontStyle: "normal",
      fontWeight: "400",
      fontVariant: "normal",
      lineHeight: "1",
      verticalAlign: "-.125em",
      color: "grey",
    }),
  };
};

const styleOverrides: StylesConfig<AgentOption> = {
  container: (baseStyles) => ({
    ...baseStyles,
    minWidth: "300px",
  }),
  // The menu is portaled to document.body (see menuPortalTarget below), so
  // it needs a z-index above Bootstrap's overlays to stay visible.
  menuPortal: (baseStyles) => ({
    ...baseStyles,
    zIndex: 1056,
  }),
  option: (baseStyles, { isSelected, data }) => ({
    ...baseStyles,
    fontFamily: data.friendlyName ? undefined : "monospace",
    fontSize: data.friendlyName ? undefined : ".875em",
    color: data.friendlyName
      ? isSelected
        ? "white"
        : "inherit"
      : isSelected
        ? "white"
        : "var(--bs-code-color)",
    ":before": {
      content: data.emoji ? `"${data.emoji}"` : `"\\f4d7"`, // bi-person-circle
    },
    "::before": getIconStyling(!!data.emoji),
  }),
  singleValue: (baseStyles, { data }) => ({
    ...baseStyles,
    fontFamily: data.friendlyName ? undefined : "monospace",
    fontSize: data.friendlyName ? undefined : ".875em",
    color: data.friendlyName ? undefined : "var(--bs-code-color)",
    ":before": {
      content: data.emoji ? `"${data.emoji}"` : `"\\f4d7"`, // bi-person-circle
    },
    "::before": getIconStyling(!!data.emoji),
  }),
};

const AgentSelect = ({
  agents,
  value,
  isLoading,
  isDisabled,
  inputId,
  placeholder,
  onChangeValue,
}: {
  agents: Array<CommonAgentSummary>;
  value: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  // Forwarded to react-select so a <label htmlFor> can target the control.
  inputId?: string;
  // Shown when `value` is empty. Setting this also keeps the select rendered
  // with no selection or no agents (otherwise those render nothing).
  placeholder?: string;
  onChangeValue: (agentId: string) => void;
}) => {
  const theme = useReactSelectTheme();
  const options: Array<AgentOption> = agents.map((agent) => {
    return {
      value: agent.id,
      friendlyName: getFriendlyAgentName(agent),
      emoji: agent.emoji,
      label: getFriendlyAgentName(agent) || agent.id,
    };
  });

  return (
    (placeholder ? true : agents.length > 0 && !!value) && (
      <Select
        inputId={inputId}
        // `?? null` keeps the select controlled when nothing is selected, so
        // clearing the value shows the placeholder instead of a stale option.
        value={options.find((option) => option.value === value) ?? null}
        placeholder={placeholder}
        // Render the menu at the document root so it overlays whatever
        // contains the select (e.g. a Card) instead of scrolling inside it.
        menuPortalTarget={
          typeof document === "undefined" ? null : document.body
        }
        styles={styleOverrides}
        theme={theme}
        isClearable={false}
        isLoading={isLoading}
        isDisabled={isLoading || isDisabled}
        options={options}
        onChange={(newValue) => {
          const agentId = (newValue as { value: string })?.value;
          onChangeValue(agentId);
        }}
      />
    )
  );
};

export default AgentSelect;
